import { useState, useEffect, useRef } from 'react';
import { highlightParts, foldTr } from '../lib/employeeSearch';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, Check, X, Clock, Calendar, AlertTriangle, FileText, Loader2, Mail, Search, Paperclip, History, CalendarClock, EyeOff } from 'lucide-react';
import { TablePagination } from '../components/TablePagination';

// Ortak yardımcı fonksiyonlar ve sabitler
import { formatDate, calculateOverallStatus } from '../lib/helpers';
import { HAZARD_CLASSES, COMPANY_DOCS, COMPANY_PROFILE_DOCS, getHazardClassInfo } from '../components/companies/constants';
import { DocumentViewerDialog } from '../components/employees/DocumentViewerDialog';
import { CompanyPassiveDialog, PassiveUntilDialog, passiveDaysLabel } from '../components/companies/CompanyPassiveDialog';
import { PassiveHistoryDialog } from '../components/companies/PassiveHistory';
import { Switch } from '../components/ui/switch';
import { RegistrationStatusBadge, RegistrationReviewDialog, hasPendingFirmDocs, firmDocSummary } from '../components/companies/RegistrationReview';
import { confirmDialog } from '../components/ConfirmHost';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { useEmailCheck, EmailCheckHint } from '../components/EmailCheckHint';
import { useScreenReady } from '../lib/perfScreen';

const CompaniesPage = () => {
  const { user, hasLocationAccess, canCreate, canEdit, canDelete } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [companyStatuses, setCompanyStatuses] = useState([]);
  const [statusError, setStatusError] = useState(false);
  const [companyStatusFilter, setCompanyStatusFilter] = useState('all');
  const [registrationFilter, setRegistrationFilter] = useState('all');
  const [reviewCompany, setReviewCompany] = useState(null);
  const [passiveCompany, setPassiveCompany] = useState(null);
  const [historyCompany, setHistoryCompany] = useState(null);
  const [untilCompany, setUntilCompany] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showDocs, setShowDocs] = useState(() => localStorage.getItem('companies_show_docs') === '1');
  const toggleDocs = () => setShowDocs(v => { localStorage.setItem('companies_show_docs', v ? '0' : '1'); return !v; });
  const [loading, setLoading] = useState(true);
  useScreenReady('firma_ekrani', !loading);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [companySearchQuery, setCompanySearchQuery] = useState(''); // Firma arama
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [pendingRegistrations, setPendingRegistrations] = useState(0);
  const fetchSeq = useRef(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const [formData, setFormData] = useState({ 
    name: '', 
    location_id: '',
    hazard_class: 'tehlikeli',
    contact_name: '',
    contact_surname: '',
    contact_email: '',
    contact_phone: '',
    activity_description: '',
    company_status_id: '',
    documents: {}
  });
  
  // For document status popover
  const [openPopover, setOpenPopover] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [showDeficiencyList, setShowDeficiencyList] = useState(false);
  const [selectedDocKey, setSelectedDocKey] = useState(null);
  const [selectedCompanyForDoc, setSelectedCompanyForDoc] = useState(null);
  const [tempDate, setTempDate] = useState('');
  const [dateWarning, setDateWarning] = useState(null);
  const [selectedDeficiencies, setSelectedDeficiencies] = useState([]); // Çoklu eksiklik seçimi
  
  // For PDF report
  const [selectedForReport, setSelectedForReport] = useState([]);
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // For email modal
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [toEmails, setToEmails] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Kullanıcının yetkili olduğu lokasyonları filtrele

  // Highlight matching text
  const highlightMatch = (text, query) => highlightParts(text, query).map((part, i) =>
    typeof part === 'string' ? part : <span key={i} className="text-red-600 font-bold">{part.text}</span>
  );

  useEffect(() => {
    fetchData();
  }, []);

  // Arama sunucuya 300 ms gecikmeyle gider; filtre değişince 1. sayfaya dönülür
  useEffect(() => {
    const t = setTimeout(() => {
      const next = companySearchQuery.length >= 3 ? companySearchQuery : '';
      if (next !== debouncedQuery) { setDebouncedQuery(next); setCurrentPage(1); }
    }, 300);
    return () => clearTimeout(t);
  }, [companySearchQuery, debouncedQuery]);

  // İlk yükleme fetchData içinde yapılır; burada yalnız filtre/sayfa değişimleri (çift istek olmasın)
  const skipFirstFetch = useRef(true);
  useEffect(() => {
    if (skipFirstFetch.current) { skipFirstFetch.current = false; return; }
    fetchCompanies();
  }, [currentPage, pageSize, debouncedQuery, activeFilter, registrationFilter, companyStatusFilter]);

  const fetchData = async () => {
    try {
      const [locationsRes, templatesRes, statusesRes] = await Promise.all([
        axios.get(`${API}/locations`),
        axios.get(`${API}/deficiency-templates`),
        axios.get(`${API}/company-statuses`)
      ]);
      setLocations(locationsRes.data);
      setCompanyStatuses(statusesRes.data);
      setTemplates(templatesRes.data);
      await fetchCompanies();
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    const seq = ++fetchSeq.current;
    try {
      let params = `page=${currentPage}&page_size=${pageSize}&exclude_stores=true&active=${activeFilter}`;
      if (registrationFilter !== 'all') params += `&registration_status=${encodeURIComponent(registrationFilter)}`;
      if (companyStatusFilter !== 'all') params += `&company_status_id=${encodeURIComponent(companyStatusFilter)}`;
      if (debouncedQuery.length >= 3) params += `&q=${encodeURIComponent(debouncedQuery)}`;
      const [response, pendingRes] = await Promise.all([
        axios.get(`${API}/companies?${params}`),
        axios.get(`${API}/companies?exclude_stores=true&registration_status=pending&page_size=1`)
      ]);
      if (seq !== fetchSeq.current) return; // eski istek yeni sonucun üzerine yazmasın
      setPendingRegistrations(pendingRes.data.total || 0);
      const data = response.data;
      setCompanies(data.items || data);
      setTotalItems(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      if (seq !== fetchSeq.current) return;
      toast.error('Firmalar yüklenemedi');
    }
  };

  // Sayfa değiştiğinde
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Sayfa boyutu değiştiğinde
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const companyEmailCheck = useEmailCheck(dialogOpen ? formData.contact_email : '', { original: editingCompany?.contact_email, excludeCompanyId: editingCompany?.id });

  // Filtrelenmiş firmalar
  const filteredCompanies = companies.filter(company => {
    // Status filter
    const overall = getApprovalStatus(company);
    if (selectedStatusFilter !== 'all' && overall.status !== selectedStatusFilter) return false;
    // Aktif/pasif, kayıt durumu, firma statüsü ve ad araması sunucuda (tüm kayıtlarda)
    return true;
  });

  const getTemplatesForDoc = (docKey) => {
    return templates.filter(t => t.document_type === docKey);
  };

  const getCompanyStatusBadge = (company) => {
    const st = companyStatuses.find(c => c.id === company.company_status_id);
    if (!st) return <span className="text-slate-400 text-sm">-</span>;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-700 font-normal whitespace-nowrap" data-testid={`company-status-badge-${company.id}`}>{st.name}</Badge>
        </TooltipTrigger>
        {st.description && <TooltipContent side="top" className="bg-slate-800 text-white max-w-xs">{st.description}</TooltipContent>}
      </Tooltip>
    );
  };

  const handleOpenDialog = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setFormData({ 
        name: company.name, 
        location_id: '',
        hazard_class: company.hazard_class,
        contact_name: company.contact_name || '',
        contact_surname: company.contact_surname || '',
        contact_email: company.contact_email || '',
        contact_phone: company.contact_phone || '',
        activity_description: company.activity_description || '',
        company_status_id: company.company_status_id || '',
        documents: company.documents || {}
      });
    } else {
      setEditingCompany(null);
      setFormData({ 
        name: '', 
        location_id: '',
        hazard_class: 'tehlikeli',
        contact_name: '',
        contact_surname: '',
        contact_email: '',
        contact_phone: '',
        company_status_id: '',
        documents: {}
      });
    }
    setStatusError(false);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_status_id) {
      setStatusError(true);
      toast.error('Firma Statüsü seçilmesi zorunludur');
      document.querySelector('[data-testid="company-status-select"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    try {
      if (editingCompany) {
        await axios.put(`${API}/companies/${editingCompany.id}`, { ...formData, company_status_id: formData.company_status_id || null });
        toast.success('Firma güncellendi. Personel geçerlilik tarihleri yeniden hesaplandı.');
        setDialogOpen(false);
      } else {
        await axios.post(`${API}/companies`, { ...formData, company_status_id: formData.company_status_id || null });
        toast.success('Firma kaydedildi');
        setFormData({ 
          name: '', 
          location_id: '',
          hazard_class: 'tehlikeli',
          contact_name: '',
          contact_surname: '',
          contact_email: '',
          contact_phone: '',
          company_status_id: formData.company_status_id,
          documents: {}
        });
      }
      fetchCompanies();
    } catch (error) {
      const message = error.response?.data?.detail || 'İşlem başarısız';
      toast.error(message);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog({ title: 'Firma silinsin mi?', description: 'Bu firmayı silmek istediğinizden emin misiniz?', confirmLabel: 'Evet, Sil' }))) return;
    try {
      await axios.delete(`${API}/companies/${id}`);
      toast.success('Firma silindi');
      fetchCompanies();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  // Handle document status change directly
  const handleDocStatusChange = async (company, docKey, newStatus, docConfig) => {
    if (newStatus === 'eksik') {
      setSelectedCompanyForDoc(company);
      setSelectedDocKey(docKey);
      setShowDeficiencyList(true);
      setOpenPopover(null);
    } else if (newStatus === 'onay') {
      // Check if date is required - use tempDate first, then existing date
      const dateToSave = tempDate || company.documents?.[`${docKey}_date`];
      if (docConfig.hasDate && !dateToSave) {
        setDateWarning('Onay vermek için önce tarih girmelisiniz!');
        return;
      }
      
      // Direct update with approval
      try {
        const updatedDocs = {
          ...company.documents,
          [docKey]: newStatus,
          [`${docKey}_note`]: '',
          [`${docKey}_date`]: dateToSave
        };
        
        await axios.put(`${API}/companies/${company.id}`, {
          name: company.name,
          location_id: company.location_id,
          hazard_class: company.hazard_class,
          documents: updatedDocs
        });
        
        toast.success('Belge onaylandı');
        setOpenPopover(null);
        setTempDate('');
        fetchCompanies();
      } catch (error) {
        toast.error('Güncelleme başarısız');
      }
    } else {
      // Beklemede - no date required
      try {
        const updatedDocs = {
          ...company.documents,
          [docKey]: newStatus,
          [`${docKey}_note`]: ''
        };
        
        await axios.put(`${API}/companies/${company.id}`, {
          name: company.name,
          location_id: company.location_id,
          hazard_class: company.hazard_class,
          documents: updatedDocs
        });
        
        toast.success('Belge durumu güncellendi');
        setOpenPopover(null);
        fetchCompanies();
      } catch (error) {
        toast.error('Güncelleme başarısız');
      }
    }
  };

  // Handle date change
  const handleDateSave = async (company, docKey) => {
    if (!tempDate) {
      toast.error('Lütfen tarih girin');
      return;
    }
    
    try {
      const updatedDocs = {
        ...company.documents,
        [`${docKey}_date`]: tempDate
      };
      
      await axios.put(`${API}/companies/${company.id}`, {
        name: company.name,
        location_id: company.location_id,
        hazard_class: company.hazard_class,
        documents: updatedDocs
      });
      
      toast.success('Tarih kaydedildi');
      setTempDate('');
      fetchCompanies();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  // Toggle deficiency checkbox
  const toggleDeficiency = (templateText) => {
    setSelectedDeficiencies(prev => 
      prev.includes(templateText) 
        ? prev.filter(t => t !== templateText)
        : [...prev, templateText]
    );
  };

  // Handle deficiency save (multiple)
  const handleDeficiencySave = async () => {
    if (!selectedCompanyForDoc || !selectedDocKey || selectedDeficiencies.length === 0) return;
    
    try {
      const deficiencyText = selectedDeficiencies.join(', ');
      const updatedDocs = {
        ...selectedCompanyForDoc.documents,
        [selectedDocKey]: 'eksik',
        [`${selectedDocKey}_note`]: deficiencyText
      };
      
      // Eksik durumunda tarih bilgisini sil
      delete updatedDocs[`${selectedDocKey}_date`];
      
      await axios.put(`${API}/companies/${selectedCompanyForDoc.id}`, {
        name: selectedCompanyForDoc.name,
        location_id: selectedCompanyForDoc.location_id,
        hazard_class: selectedCompanyForDoc.hazard_class,
        documents: updatedDocs
      });
      
      setShowDeficiencyList(false);
      setSelectedCompanyForDoc(null);
      setSelectedDocKey(null);
      setSelectedDeficiencies([]);
      fetchCompanies();
    } catch (error) {
      console.error('Güncelleme başarısız');
    }
  };

  const getHazardBadge = (hazardClass) => {
    const hazard = HAZARD_CLASSES.find(h => h.value === hazardClass);
    return hazard ? (
      <Badge className={hazard.color}>{hazard.label}</Badge>
    ) : null;
  };

  const getOverallStatusBadge = (company) => {
    // Kayıt başvurusu onay bekliyor ise simge; tıklayınca başvuru detayı açılır
    if (company.registration_status === 'pending') {
      return <RegistrationStatusBadge company={company} onClick={() => setReviewCompany(company)} />;
    }
    const overall = getApprovalStatus(company);
    const Icon = overall.status === 'onay' ? Check : overall.status === 'eksik' ? X : Clock;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            className={`relative ${overall.color} cursor-pointer hover:opacity-80`}
            onClick={() => setReviewCompany(company)}
            data-testid={`overall-status-${company.id}`}
          >
            {hasPendingFirmDocs(company) && <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-500" data-testid={`pending-docs-dot-${company.id}`} />}
            <Icon className="h-3.5 w-3.5" />
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="right" className="bg-slate-800 text-white">
          <p className="font-semibold">Kayıt belgeleri: {overall.label}</p>
          {firmDocSummary(company).map(d => <p key={d.label} className="text-xs text-slate-300">{d.label}: {d.text}</p>)}
          <p className="text-xs text-slate-400 mt-1">Detay için tıklayın</p>
        </TooltipContent>
      </Tooltip>
    );
  };

  // Firmanın onay statüsü: kayıt kararı (onay/red) doğrudan gösterilir, yoksa belge durumundan hesaplanır
  function getApprovalStatus(company) {
    if (company.registration_status === 'approved') return { status: 'onay', label: 'Onaylı', color: 'bg-emerald-100 text-emerald-700' };
    if (company.registration_status === 'rejected') return { status: 'eksik', label: 'Reddedildi', color: 'bg-rose-100 text-rose-700' };
    if (company.registration_status === 'pending') return { status: 'beklemede', label: 'Onay Bekliyor', color: 'bg-amber-100 text-amber-700' };
    return calculateOverallStatus(company.documents, COMPANY_PROFILE_DOCS.map(d => d.key));
  }

  // Eksik veya beklemede olan firmaları kontrol et
  const isCompanyDeficient = (company) => {
    const docKeys = COMPANY_PROFILE_DOCS.map(d => d.key);
    const overall = calculateOverallStatus(company.documents, docKeys);
    return overall.status === 'eksik' || overall.status === 'beklemede';
  };

  // Checkbox toggle
  const toggleReportSelection = (companyId) => {
    setSelectedForReport(prev => 
      prev.includes(companyId) 
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  // Tümünü seç/kaldır
  const toggleSelectAll = () => {
    const deficientCompanies = filteredCompanies.filter(isCompanyDeficient);
    const allSelected = deficientCompanies.every(c => selectedForReport.includes(c.id));
    
    if (allSelected) {
      setSelectedForReport(prev => prev.filter(id => !deficientCompanies.map(c => c.id).includes(id)));
    } else {
      const newSelection = [...new Set([...selectedForReport, ...deficientCompanies.map(c => c.id)])];
      setSelectedForReport(newSelection);
    }
  };

  // PDF rapor oluştur
  const generatePdfReport = async () => {
    if (selectedForReport.length === 0) {
      toast.error('Lütfen rapor için firma seçin');
      return;
    }
    
    setGeneratingReport(true);
    try {
      const response = await axios.post(`${API}/reports/company-deficiency-pdf`, {
        company_ids: selectedForReport
      }, { responseType: 'blob' });
      
      // Download the PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `firma_eksik_rapor_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF rapor oluşturuldu');
      setSelectedForReport([]);
    } catch (error) {
      toast.error('Rapor oluşturulamadı');
    } finally {
      setGeneratingReport(false);
    }
  };

  // E-posta modal'ını aç
  const openEmailModal = async () => {
    if (selectedForReport.length === 0) {
      toast.error('Lütfen e-posta için firma seçin');
      return;
    }
    
    // Daha önce kullanılan e-posta adreslerini getir
    try {
      const response = await axios.get(`${API}/email-recipients`);
      setEmailRecipients(response.data || []);
    } catch (error) {
      console.error('Could not fetch recipients');
    }
    
    setEmailModalOpen(true);
  };

  // E-posta gönder
  const sendEmailReport = async () => {
    const toList = toEmails.split(',').map(e => e.trim()).filter(e => e);
    const ccList = ccEmails.split(',').map(e => e.trim()).filter(e => e);
    
    if (toList.length === 0) {
      toast.error('En az bir alıcı e-posta adresi girin');
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/send-deficiency-email`, {
        report_type: 'company',
        ids: selectedForReport,
        to_emails: toList,
        cc_emails: ccList
      });
      
      toast.success('E-posta başarıyla gönderildi');
      setEmailModalOpen(false);
      setToEmails('');
      setCcEmails('');
      setSelectedForReport([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'E-posta gönderilemedi');
    } finally {
      setSendingEmail(false);
    }
  };

  // Hızlı e-posta ekle
  const addQuickEmail = (email, field) => {
    if (field === 'to') {
      setToEmails(prev => prev ? `${prev}, ${email}` : email);
    } else {
      setCcEmails(prev => prev ? `${prev}, ${email}` : email);
    }
  };

  const getDocStatusBadge = (company, docKey, docConfig) => {
    const status = company.documents?.[docKey];
    const date = company.documents?.[`${docKey}_date`];
    const note = company.documents?.[`${docKey}_note`];
    
    let badgeClass = '';
    let statusLabel = '';
    let Icon = Clock;
    
    switch (status) {
      case 'onay':
        badgeClass = 'bg-emerald-100 text-emerald-700';
        statusLabel = 'Onay';
        Icon = Check;
        break;
      case 'eksik':
        badgeClass = 'bg-rose-100 text-rose-700';
        statusLabel = 'Eksik';
        Icon = X;
        break;
      default:
        badgeClass = 'bg-amber-100 text-amber-700';
        statusLabel = 'Beklemede';
        Icon = Clock;
    }

    // Tooltip içeriği oluştur - sadece onaylı durumda tarih göster
    const tooltipLines = [statusLabel];
    if (status === 'onay' && date) tooltipLines.push(`Tarih: ${formatDate(date)}`);
    if (status === 'eksik' && note) tooltipLines.push(`Not: ${note}`);

    const hasFile = !!(company.approved_document_files?.[docKey] || company.document_files?.[docKey]);
    // Firmalar sayfasında sadece görüntüleme: tıklayınca belge ve durum gösterilir
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            className={`${badgeClass} hover:opacity-80 cursor-pointer transition-all relative`}
            onClick={() => setViewerDoc({ company, docKey, label: docConfig.label })}
            data-testid={`doc-badge-${company.id}-${docKey}`}
          >
            <Icon className="h-3.5 w-3.5" />
            {hasFile && <Paperclip className="h-2.5 w-2.5 absolute -top-1 -right-1 text-blue-600" data-testid={`doc-file-indicator-${company.id}-${docKey}`} />}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-slate-800 text-white max-w-xs">
          {tooltipLines.map((line, i) => (
            <p key={i} className={i > 0 ? 'text-slate-300 text-xs mt-1' : ''}>{line}</p>
          ))}
        </TooltipContent>
      </Tooltip>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
    <div data-testid="companies-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Firmalar</h1>
          <p className="text-slate-500 mt-1">Firma kayıtlarını ve belgelerini yönetin</p>
          {pendingRegistrations > 0 && (
            <button
              type="button"
              onClick={() => { setRegistrationFilter('pending'); setCurrentPage(1); }}
              className="mt-2 inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-200"
              data-testid="pending-registrations-alert"
            >
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              {pendingRegistrations} firma kaydı onay bekliyor
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {selectedForReport.length > 0 && (
            <>
              <Button 
                onClick={generatePdfReport}
                variant="outline"
                className="border-rose-200 text-rose-600 hover:bg-rose-50"
                disabled={generatingReport}
                data-testid="generate-report-btn"
              >
                {generatingReport ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Oluşturuluyor...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    PDF İndir ({selectedForReport.length})
                  </>
                )}
              </Button>
              <Button 
                onClick={openEmailModal}
                variant="outline"
                className="border-blue-200 text-blue-600 hover:bg-blue-50"
                data-testid="email-report-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                E-posta Gönder ({selectedForReport.length})
              </Button>
            </>
          )}
          {canCreate('companies') && (
            <Button 
              onClick={() => handleOpenDialog()}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="add-company-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Firma Ekle
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-2">
          <Label>Onay Statüsü:</Label>
          <Select value={selectedStatusFilter} onValueChange={setSelectedStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="Durum seçin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="eksik">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                  Eksik
                </span>
              </SelectItem>
              <SelectItem value="beklemede">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  Beklemede
                </span>
              </SelectItem>
              <SelectItem value="onay">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  Onaylı
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <Label>Kayıt Durumu:</Label>
          <Select value={registrationFilter} onValueChange={(v) => { setRegistrationFilter(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-40" data-testid="registration-filter">
              <SelectValue placeholder="Seçin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="pending">Onay Bekleyen</SelectItem>
              <SelectItem value="rejected">Reddedilen</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button variant="outline" size="sm" onClick={toggleDocs} className="gap-1.5" data-testid="toggle-company-docs-btn">
          {showDocs ? <EyeOff className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
          {showDocs ? 'Belgeleri Gizle' : 'Belgeleri Göster'}
        </Button>

        <div className="flex items-center gap-2">
          <Label>Aktiflik:</Label>
          <Select value={activeFilter} onValueChange={(v) => { setActiveFilter(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-40" data-testid="company-active-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="active">Sadece aktif firmalar</SelectItem>
              <SelectItem value="passive">Sadece pasif firmalar</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Label>Firma Statüsü:</Label>
          <Select value={companyStatusFilter} onValueChange={(v) => { setCompanyStatusFilter(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-44" data-testid="company-status-filter">
              <SelectValue placeholder="Seçin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {companyStatuses.map(st => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}
              <SelectItem value="none">Statü Belirtilmemiş</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2 max-w-[200px]">
          <Label>Firma Ara:</Label>
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="min 3 harf"
              value={companySearchQuery}
              onChange={(e) => setCompanySearchQuery(e.target.value)}
              className="pl-8"
              data-testid="company-search"
            />
          </div>
        </div>
      </div>
      
      {/* Results info */}
      <div className="text-sm text-slate-500">
        {filteredCompanies.length} / {companies.length} firma gösteriliyor
        {companySearchQuery.length >= 3 && <span className="ml-2 text-red-600">• &ldquo;{companySearchQuery}&rdquo; araması aktif</span>}
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10">
                  {filteredCompanies.some(isCompanyDeficient) && (
                    <Checkbox
                      checked={filteredCompanies.filter(isCompanyDeficient).every(c => selectedForReport.includes(c.id))}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  )}
                </TableHead>
                <TableHead className="font-semibold">Onay Statüsü</TableHead>
                <TableHead className="font-semibold">Firma</TableHead>
                <TableHead className="font-semibold">Firma Statüsü</TableHead>
                <TableHead className="font-semibold">Yetkili</TableHead>
                <TableHead className="font-semibold">İletişim</TableHead>
                <TableHead className="font-semibold">Sınıfı</TableHead>
                {showDocs && COMPANY_PROFILE_DOCS.map(doc => <TableHead key={doc.key} className="font-semibold whitespace-nowrap text-center">{doc.label}</TableHead>)}
                <TableHead className="font-semibold text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCompanies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showDocs ? 11 : 8} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <Building2 className="h-8 w-8 text-slate-300" />
                      {companies.length === 0 ? (
                        <>
                          <p className="text-slate-500">Henüz firma eklenmemiş</p>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleOpenDialog()}
                          >
                            İlk firmayı ekle
                          </Button>
                        </>
                      ) : (
                        <>
                          <p className="text-slate-500">Bu filtreye uygun firma bulunamadı</p>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setSelectedStatusFilter('all')}
                          >
                            Tüm firmaları göster
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCompanies.map((company) => (
                  <TableRow key={company.id} className="table-row-hover">
                    <TableCell>
                      {isCompanyDeficient(company) && (
                        <Checkbox
                          checked={selectedForReport.includes(company.id)}
                          onCheckedChange={() => toggleReportSelection(company.id)}
                          data-testid={`select-company-${company.id}`}
                        />
                      )}
                    </TableCell>
                    <TableCell>{getOverallStatusBadge(company)}</TableCell>
                    <TableCell className={`font-medium ${debouncedQuery.length >= 3 && foldTr(company.name).includes(foldTr(debouncedQuery)) ? 'text-red-600' : ''}`}>
                      {highlightMatch(company.name, companySearchQuery)}
                      {company.is_passive && (
                        <p className="text-[11px] font-normal text-rose-600" data-testid={`company-passive-label-${company.id}`}>
                          Pasif{company.passive_until ? ` (${company.passive_until.split('-').reverse().join('.')} tarihine kadar)` : ''}{company.passive_reason ? `: ${company.passive_reason}` : ''}
                          <span className={`ml-1.5 inline-block rounded px-1.5 py-px text-[10px] font-medium ${company.passive_until ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}`} data-testid={`company-passive-days-${company.id}`}>
                            {passiveDaysLabel(company.passive_until)}
                          </span>
                        </p>
                      )}
                      {company.subcontractors?.length > 0 && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-normal text-sky-700 hover:bg-sky-100" data-testid={`company-subcontractors-${company.id}`}>
                              Adına talep açılan firmalar ({company.subcontractors.length})
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-96 p-0" data-testid={`company-subcontractors-popover-${company.id}`}>
                            <p className="px-3 py-2 text-xs font-semibold text-slate-600 border-b">{company.name} firmasının adına talep açtığı firmalar</p>
                            <ul className="max-h-72 overflow-y-auto py-1">
                              {(company.sub_firms || []).map(sub => (
                                <li key={sub.id} className="px-3 py-2 border-b last:border-b-0" data-testid={`company-sub-firm-${sub.id}`}>
                                  <p className="text-sm font-medium text-slate-800">{sub.name} <span className="text-xs font-normal text-sky-700">adına</span></p>
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {COMPANY_DOCS.map(d => {
                                      const st = sub.documents?.[d.key];
                                      const has = !!sub.document_files?.[d.key];
                                      const [txt, cls] = st === 'onay' ? ['Onaylı', 'bg-emerald-50 text-emerald-700 border-emerald-200'] : st === 'eksik' ? ['Eksik/Hatalı', 'bg-rose-50 text-rose-700 border-rose-200'] : has ? ['Onay Bekliyor', 'bg-amber-50 text-amber-700 border-amber-200'] : ['Yüklenmedi', 'bg-slate-50 text-slate-500 border-slate-200'];
                                      return <span key={d.key} className={`rounded border px-1.5 py-0.5 text-[10px] ${cls}`} data-testid={`company-sub-firm-doc-${sub.id}-${d.key}`}><b className="font-medium">{d.label}:</b> {txt}</span>;
                                    })}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </PopoverContent>
                        </Popover>
                      )}
                    </TableCell>
                    <TableCell>{getCompanyStatusBadge(company)}</TableCell>
                    <TableCell>
                      {company.contact_name || company.contact_surname ? (
                        <span className="text-sm">{company.contact_name} {company.contact_surname}</span>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col text-xs">
                        {company.contact_email && (
                          <a href={`mailto:${company.contact_email}`} className="text-blue-600 hover:underline truncate max-w-32" title={company.contact_email}>
                            {company.contact_email}
                          </a>
                        )}
                        {company.contact_phone && (
                          <a href={`tel:${company.contact_phone}`} className="text-slate-600">{company.contact_phone}</a>
                        )}
                        {!company.contact_email && !company.contact_phone && (
                          <span className="text-slate-400">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell data-testid={`company-hazard-${company.id}`}>{getHazardBadge(company.last_permit_hazard_class) || <span className="text-xs text-slate-400" title="Tehlike sınıfı ilk çalışma izni talebinde İSG tarafından belirlenir">Talep yok</span>}</TableCell>
                    {showDocs && COMPANY_PROFILE_DOCS.map(doc => (
                      <TableCell key={doc.key} className="text-center">{getDocStatusBadge(company, doc.key, doc)}</TableCell>
                    ))}
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canEdit('companies') && (
                          <Button variant="ghost" size="icon" title="Pasiflik Geçmişi" onClick={() => setHistoryCompany(company)} data-testid={`passive-history-btn-${company.id}`}>
                            <History className="h-4 w-4 text-slate-500" />
                          </Button>
                        )}
                        {canEdit('companies') && company.is_passive && (
                          <Button variant="ghost" size="icon" title="Süreyi Değiştir" onClick={() => setUntilCompany(company)} data-testid={`passive-until-btn-${company.id}`}>
                            <CalendarClock className="h-4 w-4 text-amber-600" />
                          </Button>
                        )}
                        {canEdit('companies') && (
                          <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none mr-1" title={company.is_passive ? `Pasif: ${company.passive_reason || ''}` : 'Firmayı pasife al'}>
                            <Switch checked={!company.is_passive} onCheckedChange={() => setPassiveCompany(company)} data-testid={`company-active-switch-${company.id}`} />
                            <span className={company.is_passive ? 'text-rose-600 font-medium' : 'text-emerald-700'}>{company.is_passive ? 'Pasif' : 'Aktif'}</span>
                          </label>
                        )}
                        {canEdit('companies') && (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleOpenDialog(company)}
                            title="Güncelle"
                            aria-label="Güncelle"
                            data-testid={`edit-company-${company.id}`}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete('companies') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(company.id)}
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            data-testid={`delete-company-${company.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Company Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCompany ? 'Firma Düzenle' : 'Yeni Firma'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Firma Adı</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Firma adını girin"
                  required
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className={statusError ? 'text-rose-600' : ''}>Firma Statüsü <span className="text-rose-600">*</span></Label>
                <Select
                  value={formData.company_status_id || undefined}
                  onValueChange={(value) => { setFormData({ ...formData, company_status_id: value }); setStatusError(false); }}
                >
                  <SelectTrigger className={statusError ? 'border-2 border-rose-500 ring-2 ring-rose-200' : ''} data-testid="company-status-select">
                    <SelectValue placeholder="Firma statüsü seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {companyStatuses.map(st => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {statusError && <p className="text-xs font-semibold text-rose-600" data-testid="company-status-error">Firma Statüsü seçmeden kayıt yapılamaz.</p>}
                {companyStatuses.find(c => c.id === formData.company_status_id)?.description && (
                  <p className="text-xs text-slate-500" data-testid="company-status-description">{companyStatuses.find(c => c.id === formData.company_status_id).description}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Tehlike Sınıfı</Label>
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2" data-testid="hazard-class-readonly">
                  {getHazardBadge(editingCompany?.last_permit_hazard_class) || <span className="text-sm text-slate-500">Henüz talep yok</span>}
                  <span className="text-xs text-slate-500">Son çalışma izni talebinde İSG'nin seçtiği sınıftan otomatik alınır; buradan değiştirilemez.</span>
                </div>
              </div>

              {/* Yetkili Bilgileri */}
              <div className="pt-4 border-t">
                <Label className="text-base font-medium text-slate-700 mb-3 block">Yetkili İletişim Bilgileri</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Yetkili Adı</Label>
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      placeholder="Adı"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_surname">Yetkili Soyadı</Label>
                    <Input
                      id="contact_surname"
                      value={formData.contact_surname}
                      onChange={(e) => setFormData({ ...formData, contact_surname: e.target.value })}
                      placeholder="Soyadı"
                      data-testid="contact-surname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">E-posta</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      placeholder="ornek@firma.com"
                      data-testid="contact-email-input"
                    />
                    <EmailCheckHint state={companyEmailCheck} testId="company-email-check" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_phone">Telefon</Label>
                    <Input
                      id="contact_phone"
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      placeholder="05XX XXX XX XX"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  <Label htmlFor="activity_description">Faaliyet / İş Tanımı</Label>
                  <Input id="activity_description" value={formData.activity_description || ''} onChange={(e) => setFormData({ ...formData, activity_description: e.target.value })} placeholder="ör. Elektrik tesisatı, pano bakım ve onarımı" data-testid="activity-description-input" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit" disabled={companyEmailCheck.status === 'taken'} className="bg-slate-900 hover:bg-slate-800" data-testid="save-company-btn">
                {editingCompany ? 'Güncelle' : 'Ekle'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Deficiency Selection Dialog */}
      <Dialog open={showDeficiencyList} onOpenChange={(open) => {
        setShowDeficiencyList(open);
        if (!open) setSelectedDeficiencies([]);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Eksiklik Seçin - {COMPANY_DOCS.find(d => d.key === selectedDocKey)?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              {getTemplatesForDoc(selectedDocKey).length === 0 ? (
                <p className="text-slate-500 text-center py-4">
                  Bu belge türü için henüz eksiklik şablonu eklenmemiş.
                  <br />
                  <span className="text-sm">Eksiklik Şablonları sayfasından ekleyebilirsiniz.</span>
                </p>
              ) : (
                getTemplatesForDoc(selectedDocKey).map((template) => (
                  <label
                    key={template.id}
                    className={`flex items-center gap-3 w-full text-left px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                      selectedDeficiencies.includes(template.template_text)
                        ? 'border-rose-300 bg-rose-50'
                        : 'border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                    }`}
                  >
                    <Checkbox
                      checked={selectedDeficiencies.includes(template.template_text)}
                      onCheckedChange={() => toggleDeficiency(template.template_text)}
                    />
                    <span className="flex-1">{template.template_text}</span>
                  </label>
                ))
              )}
            </div>
            {selectedDeficiencies.length > 0 && (
              <div className="mt-4 p-3 bg-rose-50 rounded-lg border border-rose-200">
                <p className="text-sm text-rose-700 font-medium">Seçilen eksiklikler ({selectedDeficiencies.length}):</p>
                <p className="text-sm text-rose-600 mt-1">{selectedDeficiencies.join(', ')}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeficiencyList(false);
              setSelectedDeficiencies([]);
            }}>
              İptal
            </Button>
            <Button 
              onClick={handleDeficiencySave}
              disabled={selectedDeficiencies.length === 0}
              className="bg-rose-600 hover:bg-rose-700"
            >
              <X className="h-4 w-4 mr-2" />
              Eksik Kaydet ({selectedDeficiencies.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Date Warning Dialog */}
      <AlertDialog open={!!dateWarning} onOpenChange={() => setDateWarning(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Tarih Gerekli
            </AlertDialogTitle>
            <AlertDialogDescription>
              {dateWarning}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setDateWarning(null)}>
              Anladım
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Eksik Raporu E-posta ile Gönder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              {selectedForReport.length} firma için eksik belge raporu PDF olarak e-posta ile gönderilecek.
            </p>
            
            {/* Quick Recipients */}
            {emailRecipients.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Son Kullanılan Adresler</Label>
                <div className="flex flex-wrap gap-2">
                  {emailRecipients.slice(0, 6).map((r, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => addQuickEmail(r.email, 'to')}
                      className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-md text-slate-600"
                    >
                      {r.email}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="to_emails">Alıcı (To) *</Label>
              <Input
                id="to_emails"
                value={toEmails}
                onChange={(e) => setToEmails(e.target.value)}
                placeholder="ornek@email.com, diger@email.com"
                data-testid="to-emails-input"
              />
              <p className="text-xs text-slate-400">Birden fazla adres için virgül kullanın</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cc_emails">Bilgi (CC)</Label>
              <Input
                id="cc_emails"
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                placeholder="bilgi@email.com"
                data-testid="cc-emails-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEmailModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={sendEmailReport}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={sendingEmail}
              data-testid="send-email-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Gönder
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PassiveUntilDialog company={untilCompany} onClose={() => setUntilCompany(null)} onDone={fetchCompanies} />
      <PassiveHistoryDialog company={historyCompany} onClose={() => setHistoryCompany(null)} />
      <CompanyPassiveDialog company={passiveCompany} onClose={() => setPassiveCompany(null)} onDone={fetchCompanies} />

      {reviewCompany && (
        <RegistrationReviewDialog
          company={reviewCompany}
          onClose={() => setReviewCompany(null)}
          onDone={() => { setReviewCompany(null); fetchCompanies(); }}
          onRefresh={fetchCompanies}
        />
      )}

      <DocumentViewerDialog
        open={!!viewerDoc}
        onOpenChange={(open) => !open && setViewerDoc(null)}
        entity={viewerDoc?.company}
        entityType="companies"
        subtitle={viewerDoc?.company?.name}
        docKey={viewerDoc?.docKey}
        docLabel={viewerDoc?.label}
        mode="approved"
      />
    </div>
    </TooltipProvider>
  );
};

export default CompaniesPage;
